// zod schemas for diary forms (per W2 §4.3 step 3 + §6.3).

import { z } from "zod";

// 30 mood codes: 6 categories × 5 levels.  Mirror
// `backend/app/core/mood_codes.py::DIARY_MOODS`.
export const DIARY_MOOD_CODES = [
  // excited
  "mood_excited_1",
  "mood_excited_2",
  "mood_excited_3",
  "mood_excited_4",
  "mood_excited_5",
  // happy
  "mood_happy_1",
  "mood_happy_2",
  "mood_happy_3",
  "mood_happy_4",
  "mood_happy_5",
  // calm
  "mood_calm_1",
  "mood_calm_2",
  "mood_calm_3",
  "mood_calm_4",
  "mood_calm_5",
  // sad
  "mood_sad_1",
  "mood_sad_2",
  "mood_sad_3",
  "mood_sad_4",
  "mood_sad_5",
  // angry
  "mood_angry_1",
  "mood_angry_2",
  "mood_angry_3",
  "mood_angry_4",
  "mood_angry_5",
  // other
  "mood_other_1",
  "mood_other_2",
  "mood_other_3",
  "mood_other_4",
  "mood_other_5",
] as const;

export const moodCodeSchema = z.enum(DIARY_MOOD_CODES);

// HKT date stamp in YYYY-MM-DD; future dates blocked.
export const diaryDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "日期格式為 YYYY-MM-DD")
  .refine((s) => {
    const d = new Date(s + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d.getTime() <= today.getTime();
  }, "日期不可晚於今天");

export const diaryCreateSchema = z.object({
  date: diaryDateSchema,
  title: z.string().max(100, "標題最長 100 字").optional().or(z.literal("")),
  content: z
    .string()
    .min(1, "請輸入日記內容")
    .max(50_000, "日記內容過長")
    .transform((s) => s.trim())
    .refine((s) => s.length > 0, "內容不可為空白"),
  mood: moodCodeSchema.optional().nullable(),
  is_private: z.boolean().default(false),
  media_ids: z.array(z.string().uuid()).max(20, "最多 20 張圖片").default([]),
  /** W3.1 Anniversary reverse-coupling. */
  // W3.1 shipped `anniversary` here; W50.3 D-3 adds `wishlist`. The backend enum
  // has accepted both since W2 — this validator was the narrow half, so a diary
  // created with a wish source would have been rejected client-side before it
  // ever reached an endpoint that would have taken it.
  source_type: z.enum(["anniversary", "wishlist"]).nullable().optional(),
  source_id: z.string().uuid().nullable().optional(),
}).superRefine((val, ctx) => {
  if (val.source_type && !val.source_id) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["source_id"],
      // Was 「請選擇紀念日」, which became wrong the moment `wishlist` joined the
      // enum: picking a wish with no id would be told to pick an anniversary.
      message: "請選擇來源項目",
    });
  }
  if (!val.source_type && val.source_id) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["source_type"],
      message: "請選擇來源類型",
    });
  }
});

export const diaryUpdateSchema = z.object({
  date: diaryDateSchema.optional(),
  title: z.string().max(100, "標題最長 100 字").optional().or(z.literal("")),
  content: z.string().min(1, "請輸入日記內容").max(50_000).optional(),
  mood: moodCodeSchema.optional().nullable(),
  is_private: z.boolean().optional().nullable(),
});

export const classifyMoodSchema = z.object({
  content: z.string().min(1, "請輸入要分類的文字").max(50_000),
  title: z.string().max(100).optional().or(z.literal("")),
});

export type DiaryCreateInput = z.infer<typeof diaryCreateSchema>;
export type DiaryUpdateInput = z.infer<typeof diaryUpdateSchema>;
export type ClassifyMoodInput = z.infer<typeof classifyMoodSchema>;
